import React from 'react';
import PropTypes from 'prop-types';

/**
 * Trend arrow icon for cost trend indicators (up = costs rising, down = costs falling).
 * Uses danger color for up trends and success color for down trends.
 *
 * @param {Object} props
 * @param {'up'|'down'|null} props.direction - Trend direction: 'up' for rising, 'down' for falling, null to hide
 * @returns {JSX.Element|null} Trend icon span or null when direction is null
 * @example
 * <TrendIcon direction="up" />
 * <TrendIcon direction={vendor.cost_trend} />
 */
function TrendIcon({ direction = null }) {
  if (!direction) return null;

  const modifier = direction === 'up' ? 'trend-icon--up' : 'trend-icon--down';

  return (
    <span className={`trend-icon ${modifier}`}>
      {direction === 'up' ? (
        <svg viewBox="0 0 10 8" aria-hidden="true">
          <path d="M5 0L10 8H0z" fill="currentColor" />
        </svg>
      ) : (
        <svg viewBox="0 0 10 8" aria-hidden="true">
          <path d="M5 8L0 0h10z" fill="currentColor" />
        </svg>
      )}
    </span>
  );
}

TrendIcon.propTypes = {
  /** Trend direction: 'up' for rising costs, 'down' for falling costs, null to hide */
  direction: PropTypes.oneOf(['up', 'down', null]),
};

/**
 * Chevron icon for expand/collapse toggles. Rotates 180 degrees when expanded.
 *
 * @param {Object} props
 * @param {boolean} props.expanded - Whether the associated section is expanded
 * @returns {JSX.Element} Chevron icon span
 * @example
 * <ChevronIcon expanded={isOpen} />
 * <ChevronIcon expanded={expandedRows[index]} />
 */
function ChevronIcon({ expanded = false }) {
  const classes = `chevron-icon${expanded ? ' chevron-icon--expanded' : ''}`;

  return (
    <span className={classes}>
      <svg viewBox="0 0 10 6" aria-hidden="true">
        <path d="M5 6L0 0h10z" />
      </svg>
    </span>
  );
}

ChevronIcon.propTypes = {
  /** Whether the section is expanded (rotates the chevron) */
  expanded: PropTypes.bool,
};

export { TrendIcon, ChevronIcon };
